(() => {
  const root = document.querySelector('[data-rpg-background-export]');
  if (!root) return;
  const exportRouteRequested = new URLSearchParams(window.location.search).get('view') === 'rpg-backgrounds'
    || window.location.hash === '#rpg-backgrounds';
  if (root.hidden && !exportRouteRequested) return;
  if (root.hidden) {
    root.hidden = false;
    document.querySelector('.rpg-construction')?.remove();
  }

  const COLORS = ['255,181,198', '252,254,164', '186,252,180', '182,243,252'];
  const WIGGLE_PATH = new Path2D('M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95 8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z');

  const hashString = (value) => {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };

  const seededRandom = (initialSeed) => {
    let seed = initialSeed || 1;
    return () => {
      seed += 0x6D2B79F5;
      let value = seed;
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
  };

  const drawPattern = (context, width, height, theme, patternZoom = 1) => {
    const tileWidth = Math.max(220, Math.min(320, width * 0.16)) * patternZoom;
    const scale = tileWidth / 52;
    const tileHeight = 26 * scale;
    context.save();
    context.fillStyle = theme === 'dark' ? 'rgba(222,213,255,0.024)' : 'rgba(91,60,177,0.026)';
    for (let y = -tileHeight; y < height + tileHeight; y += tileHeight) {
      for (let x = 0; x < width + tileWidth; x += tileWidth) {
        context.save();
        context.translate(x, y);
        context.scale(scale, scale);
        context.fill(WIGGLE_PATH);
        context.restore();
      }
    }
    context.restore();
  };

  const drawOrb = (context, x, y, size, color, strength) => {
    const radius = size / 2;
    const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, `rgba(${color},${strength})`);
    gradient.addColorStop(0.3, `rgba(${color},${strength * 0.42})`);
    gradient.addColorStop(0.54, `rgba(${color},0.02)`);
    gradient.addColorStop(0.74, `rgba(${color},0)`);
    gradient.addColorStop(1, `rgba(${color},0)`);
    context.fillStyle = gradient;
    context.fillRect(x - radius, y - radius, size, size);
  };

  const drawAtmosphere = (canvas, theme, exportWidth = canvas.width, exportHeight = canvas.height, options = {}) => {
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = theme === 'dark' ? '#342348' : '#fbfafd';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const scaleX = canvas.width / exportWidth;
    const scaleY = canvas.height / exportHeight;
    context.save();
    context.scale(scaleX, scaleY);
    drawPattern(context, exportWidth, exportHeight, theme, options.patternZoom || 1);

    if (options.lights === false) {
      context.restore();
      return;
    }

    const lightBands = 2;
    const widthStep = Math.round(exportWidth / 120);
    const random = seededRandom(hashString(`rpg-atmosphere:desktop:${lightBands}:${widthStep}`));
    const colorOffset = Math.floor(random() * COLORS.length);
    const baseX = [3, 50, 97];
    for (let band = 0; band < lightBands; band += 1) {
      for (let column = 0; column < 3; column += 1) {
        const index = band * 3 + column;
        const x = baseX[column] + (random() - 0.5) * 24;
        const y = ((band + 0.08 + random() * 0.84) / lightBands) * 50;
        const size = 500 * (0.72 + random() * 0.72);
        const strength = 0.13 + random() * 0.07;
        const color = COLORS[(index + colorOffset) % COLORS.length];
        drawOrb(context, exportWidth * x / 100, exportHeight * y / 100, size, color, strength);
        drawOrb(context, exportWidth * x / 100, exportHeight * (y + 50) / 100, size, color, strength);
      }
    }
    context.restore();
  };

  const downloadCanvas = (canvas, filename) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('PNG generation failed.'));
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      resolve();
    }, 'image/png');
  });

  root.querySelectorAll('[data-rpg-background-card]').forEach((card) => {
    const width = Number(card.dataset.width);
    const height = Number(card.dataset.height);
    const theme = card.dataset.theme;
    const lights = card.dataset.lights !== 'no';
    const patternZoom = Number(card.dataset.patternZoom || 1);
    const preview = card.querySelector('canvas');
    const previewRatio = width / height;
    preview.width = 720;
    preview.height = Math.round(preview.width / previewRatio);
    drawAtmosphere(preview, theme, width, height, { lights, patternZoom });

    card.querySelector('[data-rpg-background-download]').addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Rendering…';
      try {
        const output = document.createElement('canvas');
        output.width = width;
        output.height = height;
        drawAtmosphere(output, theme, width, height, { lights, patternZoom });
        const variant = lights ? '' : '-no-lights-zoomed';
        await downloadCanvas(output, `right-price-games-background-${theme}${variant}-${width}x${height}.png`);
        button.textContent = 'Downloaded!';
      } catch (error) {
        button.textContent = 'Try Again';
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = original;
        }, 1200);
      }
    });
  });
})();
